import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, BookOpen, RefreshCw, HelpCircle } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const AccountHeads = () => {

  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const session     = getSessionData();
  const companyId   = session?.companyid;
  const yearId      = session?.yearid;
  const companyName = session?.companyname || session?.company_name || '';

  // ── state ──────────────────────────────────────────────────────────────
  const [heads,         setHeads]         = useState([]);
  const [filteredHeads, setFilteredHeads] = useState([]);
  const [groups,        setGroups]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [searchTerm,    setSearchTerm]    = useState('');
  const [filterType,    setFilterType]    = useState('all');
  const [filterGroup,   setFilterGroup]   = useState('all');
  const [showModal,     setShowModal]     = useState(false);
  const [editMode,      setEditMode]      = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors,        setErrors]        = useState({});
  const [showHelp,      setShowHelp]      = useState(false);

  const [formData, setFormData] = useState({
    acheadid: null, acheadcode: '', acheadname: '', acgroupid: '',
    headorder: 0, openingbalance: 0.00, balancetype: 'Dr', statushide: false,
  });

  const accountTypes = ['Asset', 'Liability', 'Equity', 'Income', 'Expense'];

  const typeColors = {
    Asset:     { light: '#d1fae5', text: '#065f46' },
    Liability: { light: '#fee2e2', text: '#991b1b' },
    Equity:    { light: '#dbeafe', text: '#1e40af' },
    Income:    { light: '#ede9fe', text: '#5b21b6' },
    Expense:   { light: '#fef3c7', text: '#92400e' },
  };

  // ── lifecycle ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !yearId) { setLoading(false); return; }
    fetchGroups(); fetchHeads();
  }, [companyId, yearId]);

  useEffect(() => { filterHeads(); }, [searchTerm, filterType, filterGroup, heads]);

  // ── data ───────────────────────────────────────────────────────────────
  const fetchGroups = async () => {
    try {
      const res = await apiClient.get('/Acgroups.php', { params: { statushide: 0 } });
      if (res.data.success) setGroups(res.data.data || []);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
    }
  };

  const fetchHeads = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/Acheads.php', { params: { statushide: 0 } });
      if (res.data.success) setHeads(res.data.data || []);
      else alert('Failed to fetch account heads: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error connecting to server.');
    } finally { setLoading(false); }
  };

  const filterHeads = () => {
    let f = heads;
    if (filterType  !== 'all') f = f.filter(h => h.actype === filterType);
    if (filterGroup !== 'all') f = f.filter(h => String(h.acgroupid) === filterGroup);
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      f = f.filter(h =>
        (h.acheadname  || '').toLowerCase().includes(s) ||
        (h.acheadcode  || '').toLowerCase().includes(s) ||
        (h.acgroupname || '').toLowerCase().includes(s)
      );
    }
    setFilteredHeads(f);
  };

  const formatBalance = (balance, type) =>
    `${parseFloat(balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${type}`;

  // ── validation ─────────────────────────────────────────────────────────
  const validateForm = () => {
    const errs = {};
    if (!formData.acheadname.trim()) errs.acheadname = 'Account head name is required';
    if (!formData.acgroupid)         errs.acgroupid  = 'Account group is required';
    if (formData.openingbalance !== '' && isNaN(formData.openingbalance))
      errs.openingbalance = 'Opening balance must be a number';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── CRUD ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const res = await apiClient[method]('/Acheads.php', { ...formData, statushide: formData.statushide ? 1 : 0 });
      if (res.data.success) {
        if (!editMode && res.data.data?.acheadcode)
          alert(`Account head created!\nCode: ${res.data.data.acheadcode}`);
        else
          alert(editMode ? 'Account head updated!' : 'Account head created!');
        closeModal(); fetchHeads();
      } else { alert('Error: ' + res.data.message); }
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error saving account head. Please try again.');
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = (head) => {
    setFormData({
      acheadid: head.acheadid, acheadcode: head.acheadcode, acheadname: head.acheadname,
      acgroupid: head.acgroupid, headorder: head.headorder || 0,
      openingbalance: head.openingbalance || 0.00, balancetype: head.balancetype || 'Dr',
      statushide: head.statushide === 1,
    });
    setEditMode(true); setErrors({}); setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this account head? This cannot be undone if transactions exist.')) return;
    try {
      const res = await apiClient.delete('/Acheads.php', { data: { acheadid: id } });
      if (res.data.success) { alert('Account head deleted!'); fetchHeads(); }
      else alert('Error: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error deleting account head. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({ acheadid: null, acheadcode: '', acheadname: '', acgroupid: '', headorder: 0, openingbalance: 0.00, balancetype: 'Dr', statushide: false });
    setEditMode(false); setErrors({});
  };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  // shared input/select styles
  const inputCls  = (err) => `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#4f46e5] focus:shadow-[0_0_0_4px_rgba(79,70,229,0.1)] ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const selectCls = (err) => `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#4f46e5] focus:shadow-[0_0_0_4px_rgba(79,70,229,0.1)] disabled:bg-gray-100 disabled:cursor-not-allowed ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const filterSelectCls = `px-4 py-2.5 border-2 border-gray-200 rounded-lg text-sm font-semibold text-gray-800 bg-white cursor-pointer outline-none transition-all duration-200 hover:border-[#4f46e5] focus:border-[#4f46e5] focus:shadow-[0_0_0_4px_rgba(79,70,229,0.1)]`;

  // ── no session ─────────────────────────────────────────────────────────
  if (!companyId || !yearId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-gray-200">
          <BookOpen size={40} className="mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">No Session</h3>
          <p className="text-gray-500 text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  // ── render ─────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── PAGE HEADER ── */}
      <div
        className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <BookOpen size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Account Heads</h1>
            <p className="text-[13px] flex items-center gap-2" style={{ color: 'rgba(255,255,255,0.8)' }}>
              Individual ledger accounts
              {companyName && (
                <span className="bg-white/20 text-white text-[11px] font-semibold px-2.5 py-0.5 rounded-full">
                  {companyName}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => { fetchGroups(); fetchHeads(); }}
            disabled={loading}
            className="w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer
              bg-white/15 border border-white/35 text-white transition-all duration-150
              hover:bg-white/25 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh"
            style={{ color: '#fff' }}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setShowHelp(true)}
            className="w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer
              bg-white/15 border border-white/35 text-white transition-all duration-150
              hover:bg-white/25"
            title="How this page works"
            style={{ color: '#fff' }}
          >
            <HelpCircle size={16} />
          </button>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0
              text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-violet-50"
            style={{ color: NAVY }}
          >
            <Plus size={17} />
            Add Head
          </button>
        </div>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        {/* Search */}
        <div className="flex-1 min-w-[240px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#4f46e5] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(79,70,229,0.1)]
          transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search by name, code or group..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium
              placeholder:text-gray-400 placeholder:font-normal"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className={filterSelectCls}>
            <option value="all">All Types</option>
            {accountTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)} className={filterSelectCls}>
            <option value="all">All Groups</option>
            {groups.map(g => (
              <option key={g.acgroupid} value={String(g.acgroupid)}>{g.acgroupname}</option>
            ))}
          </select>
        </div>

        {/* Count */}
        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          {filteredHeads.length} {filteredHeads.length === 1 ? 'head' : 'heads'}
        </div>
      </div>

      {/* ── TABLE ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">
            Loading account heads...
          </div>
        ) : filteredHeads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <BookOpen size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-4">
              {searchTerm || filterType !== 'all' || filterGroup !== 'all'
                ? 'No heads match your filters.'
                : 'No account heads found.'}
            </p>
            {!searchTerm && filterType === 'all' && filterGroup === 'all' && (
              <button
                onClick={openAddModal}
                className="px-6 py-3 text-white font-bold rounded-lg border-0 cursor-pointer
                  transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}
              >
                Create First Head
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['#', 'Code', 'Account Head Name', 'Group', 'Type', 'Opening Balance', 'Status', 'Actions'].map((h, i) => (
                    <th
                      key={h}
                      className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit', width: i === 0 ? 40 : i === 6 || i === 7 ? 90 : undefined }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredHeads.map((head, idx) => {
                  const tc = typeColors[head.actype] || { light: '#f3f4f6', text: '#374151' };
                  return (
                    <tr
                      key={head.acheadid}
                      className="border-b border-gray-100 transition-all duration-150
                        hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#4f46e5]"
                    >
                      <td className="px-4 py-3.5 text-[12px] text-gray-400">{idx + 1}</td>
                      <td className="px-4 py-3.5">
                        <span className="font-mono text-[12px] font-semibold text-violet-700 bg-violet-100 px-2 py-0.5 rounded">
                          {head.acheadcode}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2">
                          <BookOpen size={14} className="text-violet-700 flex-shrink-0" />
                          <span className="font-semibold text-gray-800">{head.acheadname}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-[13px] text-gray-600">{head.acgroupname}</td>
                      <td className="px-4 py-3.5">
                        <span className="text-[11px] font-bold px-2.5 py-1 rounded-full"
                          style={{ background: tc.light, color: tc.text }}>
                          {head.actype}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 font-mono text-[13px] text-gray-700">
                        {formatBalance(head.openingbalance, head.balancetype)}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border-2 ${
                          head.statushide
                            ? 'bg-red-50 text-red-700 border-red-400'
                            : 'bg-emerald-50 text-emerald-700 border-emerald-400'
                        }`}>
                          {head.statushide ? 'Hidden' : 'Active'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex gap-1.5">
                          <button onClick={() => handleEdit(head)} title="Edit"
                            className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer
                              bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150
                              hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5
                              hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                            <Edit2 size={13} />
                          </button>
                          <button onClick={() => handleDelete(head.acheadid)} title="Delete"
                            className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer
                              bg-red-100 border-red-400 text-red-600 transition-all duration-150
                              hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5
                              hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}
        >
          <div
            className="bg-white rounded-2xl w-full shadow-2xl border border-gray-200 overflow-hidden max-h-[90vh] overflow-y-auto"
            style={{ maxWidth: 620, animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div
              className="flex items-center justify-between px-7 py-5"
              style={{ background: 'linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%)' }}
            >
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0"
                style={{ color: '#fff' }}>
                <BookOpen size={18} />
                {editMode ? 'Edit Account Head' : 'New Account Head'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0
                  bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-7">

              {/* Auto-code banner */}
              {!editMode ? (
                <div className="flex items-center gap-2.5 px-4 py-3 mb-5 rounded-lg text-[13.5px] font-semibold text-indigo-800
                  bg-gradient-to-r from-blue-50 to-violet-50 border border-indigo-200">
                  <span>ℹ️</span>
                  <span>Head code will be auto-generated (AH001, AH002, …)</span>
                </div>
              ) : formData.acheadcode && (
                <div className="mb-5">
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Head Code</label>
                  <input type="text" value={formData.acheadcode} disabled
                    className="w-full px-3.5 py-3 border-2 border-gray-200 rounded-lg text-sm text-gray-500 bg-gray-100 cursor-not-allowed" />
                </div>
              )}

              {/* Name */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Account Head Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.acheadname}
                  onChange={e => setFormData({ ...formData, acheadname: e.target.value })}
                  maxLength="150"
                  placeholder="e.g., Petty Cash, Bank Account - Main"
                  className={inputCls(errors.acheadname)}
                />
                {errors.acheadname && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.acheadname}</p>}
              </div>

              {/* Group */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Account Group <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.acgroupid}
                  onChange={e => setFormData({ ...formData, acgroupid: e.target.value })}
                  className={selectCls(errors.acgroupid)}
                >
                  <option value="">-- Select Account Group --</option>
                  {groups.map(g => (
                    <option key={g.acgroupid} value={g.acgroupid}>
                      {g.acgroupname} ({g.actype})
                    </option>
                  ))}
                </select>
                {errors.acgroupid && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.acgroupid}</p>}
              </div>

              {/* Opening Balance + Balance Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Opening Balance</label>
                  <input
                    type="number"
                    value={formData.openingbalance}
                    onChange={e => setFormData({ ...formData, openingbalance: e.target.value })}
                    step="0.01" min="0" placeholder="0.00"
                    className={inputCls(errors.openingbalance) + ' text-right'}
                  />
                  {errors.openingbalance && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.openingbalance}</p>}
                </div>
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Balance Type</label>
                  <select
                    value={formData.balancetype}
                    onChange={e => setFormData({ ...formData, balancetype: e.target.value })}
                    className={selectCls(false)}
                  >
                    <option value="Dr">Debit (Dr)</option>
                    <option value="Cr">Credit (Cr)</option>
                  </select>
                </div>
              </div>

              {/* Order + Status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Display Order</label>
                  <input
                    type="number" min="0"
                    value={formData.headorder}
                    onChange={e => setFormData({ ...formData, headorder: e.target.value })}
                    className={inputCls(false) + ' text-right'}
                  />
                </div>
                <div className="flex items-end pb-0.5">
                  <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50
                    border-2 border-gray-200 rounded-lg w-full hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                    <input
                      type="checkbox"
                      checked={formData.statushide}
                      onChange={e => setFormData({ ...formData, statushide: e.target.checked })}
                      className="w-5 h-5 cursor-pointer accent-[#4f46e5]"
                    />
                    <span className="text-sm font-semibold text-gray-700">Hide / Inactive</span>
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg
                    cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300
                    disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer
                    transition-all duration-200 hover:-translate-y-0.5
                    hover:shadow-[0_6px_16px_rgba(79,70,229,0.35)]
                    disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: 'linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%)', color: '#fff' }}>
                  {submitLoading ? 'Saving…' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── HELP MODAL ── */}
      {showHelp && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          onClick={() => setShowHelp(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-[600px] max-h-[85vh] flex flex-col shadow-2xl border border-gray-200 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-7 py-5" style={{ background: 'linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%)' }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide" style={{ color: '#fff', margin: 0 }}>
                <HelpCircle size={20} /> How to Use Account Heads
              </h3>
              <button onClick={() => setShowHelp(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white hover:bg-white/30 hover:rotate-90 transition-all duration-200">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-7 space-y-4">
              {[
                { title: 'What This Page Is', desc: 'Account Heads are the individual ledger accounts (e.g. Petty Cash, Bank Account) that sit under an Account Group.' },
                { title: 'Add a Head', desc: 'Click "Add Head" and give it a Name and an Account Group (both required) — the head code is generated automatically.' },
                { title: 'Opening Balance', desc: 'Set the Opening Balance and choose whether it\'s a Debit (Dr) or Credit (Cr) balance.' },
                { title: 'Order & Visibility', desc: 'Display Order controls its position in lists. Check "Hide / Inactive" to soft-hide a head without deleting it.' },
                { title: 'Search & Filter', desc: 'Use the search box to find heads by name, code, or group, and the Type/Group dropdowns to narrow the list.' },
                { title: 'Edit or Delete', desc: 'Use the icons in the Actions column. A head with existing transactions may not be deletable.' },
              ].map(({ title, desc }, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ background: 'linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%)' }}>
                    {i + 1}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-800 mb-0.5">{title}</div>
                    <div className="text-sm text-gray-600">{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end px-7 py-4 border-t border-gray-200 flex-shrink-0">
              <button onClick={() => setShowHelp(false)}
                className="px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer"
                style={{ background: 'linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%)' }}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default AccountHeads;